import React, { useState, useEffect, useMemo } from 'react';
import { tenantApi, Group, User, GroupMemberUser, EntraGroupLookupItem } from '../../services/tenantApi';
import { useAppDialog } from './AppDialog';

const errorMessage = (err: unknown, fallback: string) =>
  (err instanceof Error && err.message) ? err.message : fallback;

const isActiveUser = (user: Pick<User, 'status'>) => (user.status || '').toLowerCase() === 'active';

const statusLabel = (status?: string) => {
  const value = (status || '').trim();
  if (!value) return 'Unknown status';
  if (value.toLowerCase() === 'pendinginvitation') return 'Pending invitation';
  return value;
};

type GroupSavePayload = {
  name: string;
  type: string;
  memberUserIds: string[];
  teamLeadUserIds: string[];
  scimSourceGroups: Array<{ sourceGroupId: string; sourceDisplayName?: string }>;
};

export const GroupsManagement: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editGroup, setEditGroup] = useState<Group | null>(null);
  const [createdGroup, setCreatedGroup] = useState<Group | null>(null);
  const { alert: showAlert, confirm: showConfirm, dialog } = useAppDialog();

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const data = await tenantApi.getGroups();
      setGroups(data);
    } catch (err) {
      console.error('Failed to load groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncGroupMembers = async (groupId: string, memberUserIds: string[]) => {
    const existingMembers = await tenantApi.getGroupMembers(groupId);
    const existingSet = new Set(existingMembers.map((m) => m.userId));
    const nextSet = new Set(memberUserIds);

    for (const userId of memberUserIds) {
      if (!existingSet.has(userId)) {
        await tenantApi.addGroupMember(groupId, userId);
      }
    }

    for (const member of existingMembers) {
      if (!nextSet.has(member.userId)) {
        await tenantApi.removeGroupMember(groupId, member.userId);
      }
    }
  };

  const syncGroupTeamLeads = async (groupId: string, teamLeadUserIds: string[]) => {
    const uniqueTeamLeadIds = Array.from(new Set(teamLeadUserIds));
    await tenantApi.updateGroupTeamLeads(groupId, uniqueTeamLeadIds);
  };

  const normalizeScimSourceGroups = (sourceGroups: Array<{ sourceGroupId: string; sourceDisplayName?: string }>) => {
    const byId = new Map<string, { sourceGroupId: string; sourceDisplayName?: string }>();
    for (const raw of sourceGroups || []) {
      const sourceGroupId = (raw?.sourceGroupId || '').trim().toLowerCase();
      if (!sourceGroupId) {
        continue;
      }

      const existing = byId.get(sourceGroupId);
      const sourceDisplayName = (raw?.sourceDisplayName || '').trim();
      if (!existing) {
        byId.set(sourceGroupId, {
          sourceGroupId,
          sourceDisplayName: sourceDisplayName || undefined,
        });
      } else if (!existing.sourceDisplayName && sourceDisplayName) {
        existing.sourceDisplayName = sourceDisplayName;
      }
    }

    return Array.from(byId.values());
  };

  const syncScimMappingsForGroup = async (groupId: string, sourceGroups: Array<{ sourceGroupId: string; sourceDisplayName?: string }>) => {
    const desiredGroups = normalizeScimSourceGroups(sourceGroups);
    const desiredIds = desiredGroups.map((g) => g.sourceGroupId);
    const desiredById = new Map(desiredGroups.map((g) => [g.sourceGroupId, g]));
    const mappings = await tenantApi.getTenantScimGroupIdentities(500);
    const existingForGroup = (mappings.groups || []).filter((m) =>
      m.localGroupId === groupId && (m.sourceProvider || '').toLowerCase() === 'entra');

    const desiredSet = new Set(desiredIds);
    const existingBySource = new Map(existingForGroup.map((m) => [m.sourceGroupId.toLowerCase(), m]));

    for (const sourceGroupId of desiredIds) {
      const existing = existingBySource.get(sourceGroupId);
      const desired = desiredById.get(sourceGroupId);
      if (!existing || !existing.isActive) {
        await tenantApi.upsertTenantScimGroupIdentity({
          localGroupId: groupId,
          sourceProvider: 'Entra',
          sourceGroupId,
          sourceDisplayName: desired?.sourceDisplayName || existing?.sourceDisplayName || sourceGroupId,
          isActive: true,
        });
      } else if ((desired?.sourceDisplayName || '').trim() && desired?.sourceDisplayName !== existing.sourceDisplayName) {
        await tenantApi.upsertTenantScimGroupIdentity({
          localGroupId: groupId,
          sourceProvider: 'Entra',
          sourceGroupId,
          sourceDisplayName: desired?.sourceDisplayName,
          isActive: true,
        });
      }
    }

    for (const mapping of existingForGroup) {
      if (!mapping.isActive) {
        continue;
      }

      if (!desiredSet.has(mapping.sourceGroupId.toLowerCase())) {
        await tenantApi.upsertTenantScimGroupIdentity({
          localGroupId: groupId,
          sourceProvider: mapping.sourceProvider || 'Entra',
          sourceGroupId: mapping.sourceGroupId,
          sourceDisplayName: mapping.sourceDisplayName,
          isActive: false,
        });
      }
    }
  };

  const handleCreateGroup = async (data: GroupSavePayload) => {
    // A retry after a partial failure must update the group already created, never POST a duplicate.
    let group = createdGroup;
    if (group) {
      await tenantApi.updateGroup(group.groupId, { name: data.name, type: data.type });
    } else {
      group = await tenantApi.createGroup({ name: data.name, type: data.type });
      setCreatedGroup(group);
    }

    try {
      await syncGroupMembers(group.groupId, data.memberUserIds);
      await syncGroupTeamLeads(group.groupId, data.teamLeadUserIds);
      await syncScimMappingsForGroup(group.groupId, data.scimSourceGroups);
    } catch (err) {
      await loadGroups();
      throw new Error(
        `Group "${data.name}" was created, but its setup is incomplete: ${errorMessage(err, 'unknown error')}\n\n`
        + 'Fix the issue and select Finish Setup — the group will not be created again.');
    }

    setCreatedGroup(null);
    setShowCreateModal(false);
    await loadGroups();
  };

  const closeCreateModal = () => {
    const partial = createdGroup;
    setShowCreateModal(false);
    setCreatedGroup(null);
    if (partial) void loadGroups();
  };

  const handleUpdateGroup = async (groupId: string, data: GroupSavePayload) => {
    await tenantApi.updateGroup(groupId, { name: data.name, type: data.type });

    await syncGroupMembers(groupId, data.memberUserIds);
    await syncGroupTeamLeads(groupId, data.teamLeadUserIds);
    await syncScimMappingsForGroup(groupId, data.scimSourceGroups);

    setEditGroup(null);
    await loadGroups();
  };

  const handleDeleteGroup = async (group: Group) => {
    const confirmed = await showConfirm(
      `Delete the group "${group.name}"? Members are not deleted, but schedules, routing and team lead assignments that use this group will no longer resolve to it.`,
      { title: 'Delete group', confirmLabel: 'Delete', danger: true });
    if (!confirmed) return;
    try {
      await tenantApi.deleteGroup(group.groupId);
      await loadGroups();
    } catch (err) {
      await showAlert(errorMessage(err, 'Failed to delete group'), { title: 'Delete failed' });
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredGroups = groups.filter((group) => {
    if (!normalizedSearch) return true;
    return (group.name || '').toLowerCase().includes(normalizedSearch)
      || (group.type || '').toLowerCase().includes(normalizedSearch);
  });

  return (
    <div className="management-section groups-management-view">
      <div className="section-header">
        <h2>Groups ({groups.length})</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          + Add Group
        </button>
      </div>

      <div className="filters">
        <input
          type="search"
          className="filter-search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search groups by name or type"
          aria-label="Search groups"
        />
      </div>

      {loading ? (
        <div className="loading">Loading groups...</div>
      ) : (
        <table className="data-table groups-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Members</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map((group) => (
              <tr key={group.groupId}>
                <td>{group.name}</td>
                <td><span className="type-badge">{group.type}</span></td>
                <td>{group.memberCount || 0}</td>
                <td>
                  <div className="groups-actions-row">
                    <button className="btn-icon groups-action-btn" title="Edit" onClick={() => setEditGroup(group)}>✏️</button>
                    <button className="btn-icon groups-action-btn" title="Delete" onClick={() => handleDeleteGroup(group)}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredGroups.length === 0 && (
              <tr>
                <td colSpan={4} className="empty-row">
                  {groups.length === 0 ? 'No groups found' : 'No groups match your search'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {showCreateModal && (
        <GroupModal
          onClose={closeCreateModal}
          onSave={handleCreateGroup}
          partiallyCreated={Boolean(createdGroup)}
        />
      )}

      {editGroup && (
        <GroupModal
          group={editGroup}
          onClose={() => setEditGroup(null)}
          onSave={(data) => handleUpdateGroup(editGroup.groupId, data)}
        />
      )}

      {dialog}
    </div>
  );
};

const GroupModal: React.FC<{
  group?: Group;
  partiallyCreated?: boolean;
  onClose: () => void;
  onSave: (data: GroupSavePayload) => Promise<void>;
}> = ({ group, partiallyCreated = false, onClose, onSave }) => {
  const [name, setName] = useState(group?.name || '');
  const [type, setType] = useState(group?.type || 'OnCall');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedTeamLeadUserIds, setSelectedTeamLeadUserIds] = useState<string[]>([]);
  const [scimSourceGroupInput, setScimSourceGroupInput] = useState('');
  const [scimSourceDisplayNames, setScimSourceDisplayNames] = useState<Record<string, string>>({});
  const [entraGroupQuery, setEntraGroupQuery] = useState('');
  const [entraGroupOptions, setEntraGroupOptions] = useState<EntraGroupLookupItem[]>([]);
  const [selectedSearchGroupIds, setSelectedSearchGroupIds] = useState<string[]>([]);
  const [searchingEntraGroups, setSearchingEntraGroups] = useState(false);
  const [entraGroupSearchError, setEntraGroupSearchError] = useState<string | null>(null);
  const [entraGroupSearchApprovalUrl, setEntraGroupSearchApprovalUrl] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoadingUsers(true);
      try {
        const [usersData, membersData] = await Promise.all([
          tenantApi.getUsers({ take: 1000 }),
          group ? tenantApi.getGroupMembers(group.groupId) : Promise.resolve([] as GroupMemberUser[]),
        ]);

        const allUsers = Array.isArray(usersData?.users)
          ? usersData.users
          : Array.isArray(usersData)
            ? usersData
            : [];

        let initialTeamLeadIds: string[] = [];
        if (group?.groupId) {
          try {
            const leads = await tenantApi.getGroupTeamLeads(group.groupId);
            initialTeamLeadIds = leads.teamLeadUserIds || [];
          } catch {
            initialTeamLeadIds = [];
          }

          try {
            const scimMappings = await tenantApi.getTenantScimGroupIdentities(500);
            const activeScimMappings = (scimMappings.groups || [])
              .filter((m) => m.localGroupId === group.groupId && m.isActive && (m.sourceProvider || '').toLowerCase() === 'entra')
              .map((m) => ({ sourceGroupId: m.sourceGroupId, sourceDisplayName: m.sourceDisplayName || '' }));
            setScimSourceGroupInput(activeScimMappings.map((m) => m.sourceGroupId).join('\n'));
            setScimSourceDisplayNames(
              activeScimMappings.reduce<Record<string, string>>((acc, m) => {
                const key = (m.sourceGroupId || '').toLowerCase();
                if (key && m.sourceDisplayName) {
                  acc[key] = m.sourceDisplayName;
                }
                return acc;
              }, {}),
            );
          } catch {
            setScimSourceGroupInput('');
            setScimSourceDisplayNames({});
          }
        }

        setUsers(allUsers);
        setSelectedUserIds((membersData || []).map(m => m.userId));
        setSelectedTeamLeadUserIds(initialTeamLeadIds);
      } catch (err) {
        console.error('Failed to load group modal data:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadData();
  }, [group]);

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const isSelected = prev.includes(userId);
      const next = isSelected ? prev.filter(id => id !== userId) : [...prev, userId];
      if (isSelected) {
        setSelectedTeamLeadUserIds((leadPrev) => leadPrev.filter((id) => id !== userId));
      }
      return next;
    });
  };

  const parseScimSourceGroups = (raw: string): Array<{ sourceGroupId: string; sourceDisplayName?: string }> => {
    const tokens = raw
      .split(/[\n,;]/g)
      .map((token) => token.trim())
      .filter((token) => token.length > 0);

    const uniqueIds = Array.from(new Set(tokens.map((token) => token.toLowerCase())));
    return uniqueIds.map((sourceGroupId) => {
      const label = scimSourceDisplayNames[sourceGroupId];
      return {
        sourceGroupId,
        sourceDisplayName: label || undefined,
      };
    });
  };

  const addScimSourceGroup = (group: EntraGroupLookupItem) => {
    const nextId = (group.id || '').trim().toLowerCase();
    if (!nextId) {
      return;
    }

    setScimSourceGroupInput((prev) => {
      const existingIds = new Set(
        prev
          .split(/[\n,;]/g)
          .map((token) => token.trim().toLowerCase())
          .filter((token) => token.length > 0),
      );

      if (existingIds.has(nextId)) {
        return prev;
      }

      return prev.trim() ? `${prev.trim()}\n${nextId}` : nextId;
    });

    setScimSourceDisplayNames((prev) => ({
      ...prev,
      [nextId]: (group.displayName || '').trim() || nextId,
    }));
  };

  const removeScimSourceGroup = (sourceGroupId: string) => {
    const targetId = (sourceGroupId || '').trim().toLowerCase();
    if (!targetId) {
      return;
    }

    setScimSourceGroupInput((prev) => {
      const nextIds = prev
        .split(/[\n,;]/g)
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token.length > 0 && token !== targetId);
      return nextIds.join('\n');
    });

    setScimSourceDisplayNames((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, targetId)) {
        return prev;
      }

      const next = { ...prev };
      delete next[targetId];
      return next;
    });
  };

  const scimSourceGroupIds = useMemo(() => {
    return Array.from(new Set(
      scimSourceGroupInput
        .split(/[\n,;]/g)
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token.length > 0),
    ));
  }, [scimSourceGroupInput]);

  const scimSourceGroupLookup = useMemo(() => {
    return entraGroupOptions.reduce<Record<string, string>>((acc, option) => {
      const id = (option.id || '').trim().toLowerCase();
      const displayName = (option.displayName || '').trim();
      if (id && displayName) {
        acc[id] = displayName;
      }
      return acc;
    }, {});
  }, [entraGroupOptions]);

  const toggleSearchGroupSelection = (groupId: string) => {
    const id = (groupId || '').trim();
    if (!id) {
      return;
    }

    setSelectedSearchGroupIds((prev) => (
      prev.includes(id)
        ? prev.filter((value) => value !== id)
        : [...prev, id]
    ));
  };

  const addSelectedSearchGroups = () => {
    if (selectedSearchGroupIds.length === 0) {
      return;
    }

    const selectedSet = new Set(selectedSearchGroupIds.map((id) => id.trim().toLowerCase()).filter(Boolean));
    for (const groupOption of entraGroupOptions) {
      const normalizedId = (groupOption.id || '').trim().toLowerCase();
      if (!normalizedId || !selectedSet.has(normalizedId)) {
        continue;
      }

      addScimSourceGroup(groupOption);
    }

    setSelectedSearchGroupIds([]);
  };

  const onSearchEntraGroups = async () => {
    const q = entraGroupQuery.trim();
    if (q.length < 2) {
      setEntraGroupSearchError('Enter at least 2 characters to search Entra groups.');
      setEntraGroupOptions([]);
      return;
    }

    setSearchingEntraGroups(true);
    setEntraGroupSearchError(null);
    setEntraGroupSearchApprovalUrl(null);
    try {
      const result = await tenantApi.searchTenantScimEntraGroups(q, 20);
      setEntraGroupOptions(result.groups || []);
      setSelectedSearchGroupIds([]);
    } catch (err: any) {
      setEntraGroupOptions([]);
      setSelectedSearchGroupIds([]);
      setEntraGroupSearchError(err?.message || 'Failed to search Entra groups.');
      const approvalUrl = typeof err?.approvalUrl === 'string'
        ? err.approvalUrl
        : (typeof err?.details?.approvalUrl === 'string' ? err.details.approvalUrl : '');
      setEntraGroupSearchApprovalUrl(typeof approvalUrl === 'string' && approvalUrl.trim().length > 0
        ? approvalUrl.trim()
        : null);
    } finally {
      setSearchingEntraGroups(false);
    }
  };

  const toggleTeamLead = (userId: string) => {
    setSelectedTeamLeadUserIds((prev) => {
      const isSelected = prev.includes(userId);
      const next = isSelected ? prev.filter((id) => id !== userId) : [...prev, userId];

      if (!isSelected) {
        setSelectedUserIds((memberPrev) => memberPrev.includes(userId) ? memberPrev : [...memberPrev, userId]);
      }

      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);

    const teamLeadIds = Array.from(new Set(selectedTeamLeadUserIds));
    const usersById = new Map(users.map((user) => [user.userId, user]));
    const inactiveLeads = teamLeadIds
      .map((id) => usersById.get(id))
      .filter((user): user is User => Boolean(user) && !isActiveUser(user as User));
    if (inactiveLeads.length > 0) {
      setSaveError(`Team leads must be active users. ${inactiveLeads
        .map((user) => `${user.displayName || user.email} (${statusLabel(user.status)})`)
        .join(', ')} cannot be a team lead until they have accepted their invitation.`);
      return;
    }
    if (!group && teamLeadIds.length === 0) {
      setSaveError('Select at least one active member as Team Lead. Every group needs a team lead for swap approval and notification routing.');
      return;
    }

    setSubmitting(true);
    try {
      await onSave({
        name: name.trim(),
        type,
        memberUserIds: Array.from(new Set(selectedUserIds)),
        teamLeadUserIds: teamLeadIds,
        scimSourceGroups: parseScimSourceGroups(scimSourceGroupInput),
      });
    } catch (err) {
      setSaveError(errorMessage(err, group ? 'Failed to save group' : 'Failed to create group'));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (user.displayName || '').toLowerCase().includes(term)
      || (user.email || '').toLowerCase().includes(term);
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <h3>{group ? 'Edit Group' : 'Create Group'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Platform Team"
            />
          </div>

          <div className="form-group">
            <label>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="OnCall">On-Call</option>
              <option value="ResponderTeam">Responder Team</option>
              <option value="Platform">Platform</option>
              <option value="Custom">Custom</option>
            </select>
          </div>

          <div className="form-group">
            <label>Group Members and Team Leads</label>
            {loadingUsers ? (
              <div className="loading" style={{ margin: '0.5rem 0' }}>Loading users...</div>
            ) : (
              <>
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search users by name or email"
                  style={{ marginBottom: '0.5rem' }}
                />
                <div
                  style={{
                    border: '1px solid var(--border-color, #ddd)',
                    borderRadius: 6,
                    maxHeight: 260,
                    overflowY: 'auto',
                    padding: '0.5rem',
                    background: 'var(--card-bg)',
                  }}
                >
                {filteredUsers.length === 0 ? (
                  <div className="help-text">No users available.</div>
                ) : (
                  <>
                    <div className="help-text" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,1fr) 90px 90px', fontWeight: 600, marginBottom: '0.35rem' }}>
                      <span>User</span>
                      <span>Member</span>
                      <span>Team Lead</span>
                    </div>
                    {filteredUsers.map((user) => (
                    <div
                      key={user.userId}
                      className="checkbox-label"
                      style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,1fr) 90px 90px', alignItems: 'center', marginBottom: '0.4rem', background: 'var(--card-bg)' }}
                    >
                      <span style={{ display: 'flex', flexDirection: 'column' }}>
                        <strong style={{ fontWeight: 500 }}>{user.displayName || user.email}</strong>
                        <span className="help-text">
                          {user.email}{!isActiveUser(user) ? ` · ${statusLabel(user.status)}` : ''}
                        </span>
                      </span>
                      <span>
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.userId)}
                          onChange={() => toggleUser(user.userId)}
                        />
                      </span>
                      <span>
                        <input
                          type="checkbox"
                          aria-label={`Team lead ${user.displayName || user.email}`}
                          checked={selectedTeamLeadUserIds.includes(user.userId)}
                          disabled={!isActiveUser(user) && !selectedTeamLeadUserIds.includes(user.userId)}
                          title={isActiveUser(user) ? undefined : 'Only active users can be team leads'}
                          onChange={() => toggleTeamLead(user.userId)}
                        />
                      </span>
                    </div>
                    ))}
                  </>
                )}
                </div>
              </>
            )}
            <div className="help-text" style={{ marginTop: '0.4rem' }}>
              Team Lead selections are scoped directly to this group for swap approval and notification routing.
              {!group && ' At least one active team lead is required.'}
            </div>
          </div>

          <div className="form-group">
            <label>SCIM Source Group IDs (Entra)</label>
            <div style={{ marginBottom: '0.45rem' }}>
              <label style={{ display: 'block', marginBottom: '0.35rem' }}>Find Entra Group By Name</label>
              <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={entraGroupQuery}
                  onChange={(e) => setEntraGroupQuery(e.target.value)}
                  placeholder="Search display name (e.g. Platform Team)"
                  style={{ flex: 1, minWidth: 220 }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onSearchEntraGroups}
                  disabled={searchingEntraGroups}
                >
                  {searchingEntraGroups ? 'Searching...' : 'Search Entra'}
                </button>
              </div>
              {entraGroupSearchError && (
                <div className="warning-message" style={{ marginTop: '0.35rem' }}>
                  <div>{entraGroupSearchError}</div>
                  {entraGroupSearchApprovalUrl && (
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ marginTop: '0.4rem' }}
                      onClick={() => window.open(entraGroupSearchApprovalUrl, '_blank', 'noopener,noreferrer')}
                    >
                      Grant Entra Consent
                    </button>
                  )}
                </div>
              )}
              {entraGroupOptions.length > 0 && (
                <div
                  style={{
                    border: '1px solid var(--border-color, #ddd)',
                    borderRadius: 6,
                    maxHeight: 180,
                    overflowY: 'auto',
                    marginTop: '0.45rem',
                    padding: '0.45rem',
                    background: 'var(--card-bg)',
                  }}
                >
                  <div className="help-text" style={{ marginBottom: '0.35rem' }}>
                    Select one or more Entra groups, then click Add Selected.
                  </div>
                  {entraGroupOptions.map((groupOption) => (
                    <div
                      key={groupOption.id}
                      style={{ display: 'grid', gridTemplateColumns: '24px minmax(240px,1fr) auto', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSearchGroupIds.includes((groupOption.id || '').trim().toLowerCase())}
                        onChange={() => toggleSearchGroupSelection((groupOption.id || '').trim().toLowerCase())}
                        aria-label={`Select Entra group ${groupOption.displayName}`}
                      />
                      <div>
                        <div style={{ fontWeight: 600 }}>{groupOption.displayName}</div>
                        <div className="help-text">{groupOption.id}</div>
                      </div>
                      <button type="button" className="btn-secondary" onClick={() => addScimSourceGroup(groupOption)}>
                        Add
                      </button>
                    </div>
                  ))}
                  <div className="form-actions" style={{ marginTop: '0.35rem', marginBottom: 0, paddingTop: 0, borderTop: 0 }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={addSelectedSearchGroups}
                      disabled={selectedSearchGroupIds.length === 0}
                    >
                      Add Selected ({selectedSearchGroupIds.length})
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="help-text" style={{ marginTop: '0.4rem' }}>
              Many Entra/SCIM source groups can map to this one local group. Use Search Entra for human-friendly name lookup.
            </div>
            {scimSourceGroupIds.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem' }}>Mapped Source Groups</label>
                <div
                  style={{
                    border: '1px solid var(--border-color, #ddd)',
                    borderRadius: 6,
                    maxHeight: 180,
                    overflowY: 'auto',
                    padding: '0.45rem',
                    background: 'var(--card-bg)',
                  }}
                >
                  {scimSourceGroupIds.map((id) => {
                    const displayName = (scimSourceDisplayNames[id] || scimSourceGroupLookup[id] || '').trim();
                    return (
                      <div
                        key={id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(240px,1fr) auto',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.35rem',
                          paddingBottom: '0.35rem',
                          borderBottom: '1px solid var(--border-color-light, #eee)',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>{displayName || '(Display name unavailable)'}</div>
                          <div className="help-text">{id}</div>
                        </div>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => removeScimSourceGroup(id)}
                          aria-label={`Remove source group ${displayName || id}`}
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {saveError && (
            <div className="error-message groups-modal-error" role="alert">{saveError}</div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">{partiallyCreated ? 'Close' : 'Cancel'}</button>
            <button type="submit" className="btn-primary" disabled={submitting || loadingUsers}>
              {submitting
                ? (group || partiallyCreated ? 'Saving...' : 'Creating...')
                : (group ? 'Save Changes' : partiallyCreated ? 'Finish Setup' : 'Create Group')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
